import type {
  AddMaintenanceRequest,
  AssetCategoryResponse,
  AssetKpis,
  AssetResponseFull,
  AssetStatus,
  AssetSummary,
  CreateAssetRequest,
  DepreciationRunResponse,
  DisposalPreviewResponse,
  DisposeAssetRequest,
  MaintenanceEntry,
  RunPreviewResponse,
  UpdateAssetCategoryRequest,
  UpdateAssetRequest,
} from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { ACCOUNTS_KEY } from '@/features/accounts/api';
import { throwApiError } from '@/lib/server-messages';

/** The FIN-07 data layer. */

export const ASSETS_KEY = ['assets'] as const;

export interface AssetFilters {
  category?: string | undefined;
  status?: AssetStatus | undefined;
  search?: string | undefined;
}

export function useAssets(filters: AssetFilters = {}) {
  return useQuery({
    queryKey: [...ASSETS_KEY, 'list', filters],
    queryFn: async (): Promise<{ kpis: AssetKpis; assets: AssetSummary[] }> => {
      const response = await api.fixedAssets.listAssets({
        query: {
          ...(filters.category ? { category: filters.category as never } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.search ? { search: filters.search } : {}),
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useAsset(assetId: string | undefined) {
  return useQuery({
    queryKey: [...ASSETS_KEY, 'asset', assetId],
    enabled: Boolean(assetId),
    queryFn: async (): Promise<AssetResponseFull> => {
      const response = await api.fixedAssets.getAsset({ params: { assetId: assetId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useAssetCategories() {
  return useQuery({
    queryKey: [...ASSETS_KEY, 'categories'],
    queryFn: async (): Promise<AssetCategoryResponse[]> => {
      const response = await api.fixedAssets.listCategories();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.categories;
    },
  });
}

function useAssetMutation<TInput, TOut>(run: (input: TInput) => Promise<TOut>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ASSETS_KEY }),
  });
}

/** A write that POSTS a journal — the chart's balances moved with it. */
function usePostingMutation<TInput, TOut>(run: (input: TInput) => Promise<TOut>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ASSETS_KEY });
      void queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}

export function useCreateAsset() {
  return useAssetMutation(async (body: CreateAssetRequest): Promise<AssetResponseFull> => {
    const response = await api.fixedAssets.createAsset({ body });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useUpdateAsset() {
  return useAssetMutation(
    async (input: { assetId: string; body: UpdateAssetRequest }): Promise<AssetResponseFull> => {
      const response = await api.fixedAssets.updateAsset({
        params: { assetId: input.assetId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useUpdateCategory() {
  return useAssetMutation(
    async (input: {
      categoryId: string;
      body: UpdateAssetCategoryRequest;
    }): Promise<AssetCategoryResponse> => {
      const response = await api.fixedAssets.updateCategory({
        params: { categoryId: input.categoryId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useDepreciationPreview(month: string, enabled: boolean) {
  return useQuery({
    queryKey: [...ASSETS_KEY, 'run-preview', month],
    enabled,
    queryFn: async (): Promise<RunPreviewResponse> => {
      const response = await api.fixedAssets.previewDepreciation({ body: { month } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function usePostDepreciation() {
  return usePostingMutation(async (month: string): Promise<DepreciationRunResponse> => {
    const response = await api.fixedAssets.postDepreciation({ body: { month } });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useDepreciationRuns() {
  return useQuery({
    queryKey: [...ASSETS_KEY, 'runs'],
    queryFn: async (): Promise<DepreciationRunResponse[]> => {
      const response = await api.fixedAssets.listDepreciationRuns();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.runs;
    },
  });
}

export function useDisposalPreview(
  assetId: string | undefined,
  query: { date: string; method: 'sold' | 'scrapped' | 'donated'; proceeds?: string },
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...ASSETS_KEY, 'disposal-preview', assetId, query],
    enabled: enabled && Boolean(assetId),
    retry: false,
    queryFn: async (): Promise<DisposalPreviewResponse> => {
      const response = await api.fixedAssets.previewDisposal({
        params: { assetId: assetId! },
        query: {
          date: query.date,
          method: query.method,
          ...(query.proceeds ? { proceeds: query.proceeds } : {}),
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useDisposeAsset() {
  return usePostingMutation(
    async (input: { assetId: string; body: DisposeAssetRequest }): Promise<AssetResponseFull> => {
      const response = await api.fixedAssets.disposeAsset({
        params: { assetId: input.assetId },
        body: input.body,
      });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useAddMaintenance() {
  return useAssetMutation(
    async (input: { assetId: string; body: AddMaintenanceRequest }): Promise<MaintenanceEntry> => {
      const response = await api.fixedAssets.addMaintenance({
        params: { assetId: input.assetId },
        body: input.body,
      });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useRemoveMaintenance() {
  return useAssetMutation(
    async (input: { assetId: string; entryId: string }): Promise<MaintenanceEntry> => {
      const response = await api.fixedAssets.removeMaintenance({
        params: { assetId: input.assetId, entryId: input.entryId },
        body: {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}
